import { useEffect } from 'react';
import { get } from 'lodash';
import Cookies from 'js-cookie';

// 补全对话避免同一对话多次记载
const completeConvRequesting = {};
// 并发控制 - 最大同时进行的补全请求数
const MAX_CONCURRENT_REQUESTS = 3;
let currentRequestCount = 0;
const requestQueue = [];

function useSocketEventOn(props) {
  const {
    convAll,
    im,
    addConvs,
    removeConv,
    updateConvMessage,
    updateConvVisitorName,
    addClientTagSocket,
    removeClientTagSocket,
    visitorCome,
    visitorOffline,
    updateConvVisitInfo,
    handleColleagueToTrusteeship
  } = props;

  // 处理请求队列
  const processRequestQueue = () => {
    if (currentRequestCount >= MAX_CONCURRENT_REQUESTS || requestQueue.length === 0) {
      return;
    }

    const { conversation_id } = requestQueue.shift();
    executeCompleteRequest(conversation_id);
  };

  // 执行补全请求
  const executeCompleteRequest = conversation_id => {
    console.log('executeCompleteRequest ===', conversation_id);
    currentRequestCount++;

    // 设置10秒超时
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout')), 10000);
    });

    const requestPromise = im.promiseEmit('getConversation', {
      convId: conversation_id
    });

    Promise.race([requestPromise, timeoutPromise])
      .then(res => {
        const newConv = get(res, 'data', null);
        if (newConv) {
          // 直接添加对话到列表中，addConvs 会处理排序和去重
          addConvs([newConv]);
        }
      })
      .catch(error => {
        console.warn(`Failed to complete conversation ${conversation_id}:`, error);
      })
      .finally(() => {
        completeConvRequesting[conversation_id] = false;
        currentRequestCount--;
        // 继续处理队列中的下一个请求
        processRequestQueue();
      });
  };

  const handleAddConversation = convs => {
    if (convs && convs.length > 0) {
      const newConvs = convs.map(val => val.body);
      addConvs(newConvs);
    }
  };

  const handleRedirectConversation = stream => {
    const conv = get(stream, 'body.conversation_body', {});
    if (conv) {
      // reduce中会根据对话更新时间去重
      addConvs([conv]);
    }
  };

  const handleRemoveConversation = stream => {
    const { track_id } = stream;
    setTimeout(() => {
      removeConv(track_id);
    });
  };

  const handleUpdateConvMessage = streams => {
    if (typeof updateConvMessage === 'function') {
      setTimeout(() => {
        updateConvMessage(streams);
      });
    }
  };

  const handleAddClientTag = stream => {
    const { body } = stream;
    addClientTagSocket(body);
  };

  const handleRemoveClientTag = stream => {
    const { body } = stream;
    removeClientTagSocket(body);
  };

  const handleVisitorCome = stream => {
    if (stream && stream.length > 0 && visitorCome) {
      visitorCome(stream);
    }
  };

  const handleVisitorOffline = stream => {
    if (stream && stream.length > 0) {
      visitorOffline(stream);
    }
  };

  const handleGetMyMessage = streams => {
    handleUpdateConvMessage(streams);
  };

  const handleCheckHasConversationData = stream => {
    const { conversation_id, type } = stream;
    // ['welcome', 'ending', 'message', 'auto_reply', 'promotion', 'internal'];
    const needCheckConvType = ['message', 'auto_reply', 'promotion'];
    if (needCheckConvType.includes(type)) {
      const convItem = convAll.find(conv => conv.id === conversation_id);
      if (!convItem && !completeConvRequesting[conversation_id]) {
        completeConvRequesting[conversation_id] = true;

        if (currentRequestCount < MAX_CONCURRENT_REQUESTS) {
          // 直接执行请求
          executeCompleteRequest(conversation_id);
        } else {
          // 加入请求队列
          requestQueue.push({ conversation_id });
        }
      }
    }
  };

  const handleUpdatDataConvVisitInfo = data => {
    updateConvVisitInfo(data);
  };

  const handleChangeClientName = stream => {
    const {
      track_id,
      body: { name }
    } = stream;
    let visitorName = name;
    if (stream.body.attrs && stream.body.attrs.name) {
      visitorName = name || stream.body.attrs.name;
    }
    // 更新对话列表访客name
    updateConvVisitorName({ trackId: track_id, visitorName });
  };

  useEffect(() => {
    // 对话相关事件
    im.on('getMyConversation', handleAddConversation);
    im.on('getColConversation', handleAddConversation);
    // 转接和结束对话
    // 转接：加入我的对话或加入同事对话
    // 结束对话：结束对话
    im.on('redirect', handleRedirectConversation);
    im.on('endConversation', handleRemoveConversation);
    // 新的消息 改变对话最新消息和时间
    im.on('getMyMessage', handleGetMyMessage);
    im.on('getColleaguesMessage', handleUpdateConvMessage);
    // 对话标签的添加与移除
    im.on('clientAddTag', handleAddClientTag);
    im.on('clientRemoveTag', handleRemoveClientTag);
    // 访客的上线与下线
    im.on('visitorCome', handleVisitorCome);
    im.on('offline', handleVisitorOffline);
    im.on('updateVisitInfo', handleUpdatDataConvVisitInfo);
    // 修改访客姓名
    im.on('agentChangeName', handleChangeClientName);
    // 托管
    im.on('from_colleague_to_trusteeship', handleColleagueToTrusteeship);

    return () => {
      im.off('getMyConversation', handleAddConversation);
      im.off('getColConversation', handleAddConversation);
      im.off('redirect', handleRedirectConversation);
      im.off('endConversation', handleRemoveConversation);
      im.off('getMyMessage', handleGetMyMessage);
      im.off('getColleaguesMessage', handleUpdateConvMessage);
      im.off('clientAddTag', handleAddClientTag);
      im.off('clientRemoveTag', handleRemoveClientTag);
      im.off('visitorCome', handleVisitorCome);
      im.off('offline', handleVisitorOffline);
      im.off('updateVisitInfo', handleUpdatDataConvVisitInfo);
      im.off('agentChangeName', handleChangeClientName);
      im.off('from_colleague_to_trusteeship', handleColleagueToTrusteeship);
    };
  }, []);

  useEffect(() => {
    // 补齐对话
    im.on('checkHasConversationData', handleCheckHasConversationData);
    return () => {
      im.off('checkHasConversationData', handleCheckHasConversationData);
    };
  }, [convAll]);

  // 清理队列
  useEffect(() => {
    return () => {
      requestQueue.length = 0;
      currentRequestCount = 0;
    };
  }, []);
}

export default useSocketEventOn;
